<template>
    <div id="billing-review-queue-container">
        <card title="Billing Review">
            <b-row>
                <b-col>
                    <SearchComponent v-model="storeFilter" label="Store" search-u-r-l="/Store"
                        :params="{ active: true }" clearable prepopulate @input="search" />
                </b-col>
                <b-col>
                    <SearchComponent v-model="programFilter" label="Program" search-u-r-l="/Program"
                        :params="{ active: true }" clearable prepopulate @input="search" />
                </b-col>
            </b-row>

            <b-tabs pills content-class="mt-3" class="mt-3">
                <b-tab title="Flagged">
                    <b-table id="billing-review-table" outlined sort-icon-left show-empty
                        empty-text="No fills are waiting for billing review"
                        empty-filtered-text="No fills are waiting for billing review" :fields="reviewFields"
                        :items="pagedReviews" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
                        :no-local-sorting="true" :busy="loading" @sort-changed="handleSort">
                        <template #table-busy>
                            <div class="text-center text-danger my-2">
                                <b-spinner class="align-middle" />
                                <strong>Loading...</strong>
                            </div>
                        </template>

                        <template #cell(rxId)="data">
                            <router-link :to="prescriptionRoute(data.item)" target="_blank">
                                {{ data.value }}
                            </router-link>
                        </template>

                        <template #cell(billingReviewReason)="data">
                            {{ data.value }}
                            <b-badge v-if="!data.item.blocksOrderCreation" variant="secondary" class="ml-1">
                                not held
                            </b-badge>
                        </template>

                        <template #cell(review)="data">
                            <b-button size="sm" variant="outline-primary" @click="openDetail(data.item)">
                                <b-icon icon="clipboard-data" />
                            </b-button>
                        </template>
                    </b-table>

                    <BottomPagerBar v-model="pageNumber" :total-rows="reviews.length" :per-page="pageSize" show-total
                        item-name="fill" :show-page-size-selector="true" :loading="loading"
                        @change-page-size="pageSize = $event" />
                </b-tab>

                <b-tab title="Scheduled">
                    <b-table id="scheduled-claim-table" outlined sort-icon-left show-empty
                        empty-text="Nothing is currently scheduled"
                        empty-filtered-text="Nothing is currently scheduled" :fields="scheduledFields"
                        :items="pagedScheduled" :sort-by.sync="scheduledSortBy"
                        :sort-desc.sync="scheduledSortDesc" :no-local-sorting="true" :busy="loading"
                        @sort-changed="handleScheduledSort">
                        <template #table-busy>
                            <div class="text-center text-danger my-2">
                                <b-spinner class="align-middle" />
                                <strong>Loading...</strong>
                            </div>
                        </template>

                        <template #cell(rxId)="data">
                            <router-link :to="prescriptionRoute(data.item)" target="_blank">
                                {{ data.value }}
                            </router-link>
                        </template>

                        <!-- Plans.Name is nullable and unset on most plan rows, so a missing name says
                             nothing about whether the plan is still attached. Muted rather than danger:
                             an unnamed plan is ordinary, not something for the reviewer to act on. -->
                        <template #cell(planName)="data">
                            <span v-if="data.value">{{ data.value }}</span>
                            <span v-else class="text-muted">Unnamed plan</span>
                        </template>

                        <template #cell(expectedOutcomeLabel)="data">
                            {{ data.value }}
                            <div v-if="data.item.expectedRejectCodes" class="text-muted">
                                <small>{{ data.item.expectedRejectCodes }}</small>
                            </div>
                        </template>
                    </b-table>

                    <BottomPagerBar v-model="scheduledPageNumber" :total-rows="scheduled.length" :per-page="pageSize"
                        show-total item-name="scheduled claim" :show-page-size-selector="true" :loading="loading"
                        @change-page-size="pageSize = $event" />
                </b-tab>
            </b-tabs>

            <b-modal id="billing-review-modal" size="xl" title="Billing Review" hide-footer no-close-on-backdrop>
                <BillingReviewDetails v-if="selected" :key="selected.rxId" :store-id="selected.storeID"
                    :rx-number="selected.rxNumber" :rf-number="selected.rfNumber" @completed="onReviewCompleted" />
            </b-modal>
        </card>
    </div>
</template>

<script lang="ts">
import Axios from "axios";
import moment from "moment";
import { Component, Vue, Watch } from "vue-property-decorator";
import SearchComponent from "@/components/SearchComponent.vue";
import BottomPagerBar from "@/components/BottomPagerBar.vue";
import BillingReviewDetails from "./BillingReviewDetails.vue";
import { Store } from "@/models/Store";
import { Program } from "@/models/Program";
import { BillingReviewRow, ScheduledClaimRow } from "@/models/BillingReview";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { getPageSizePreference, savePageSizePreference } from "@/util/pageSizePreference";
import { saveQueueFilters, loadQueueFilters } from "@/util/queueFilterPersistence";
import { sortRows, pageRows } from "@/util/queueSort";

/**
 * Fills whose scheduled claims did not adjudicate as predicted, plus what is still staged.
 *
 * The Scheduled tab shares the Flagged tab's filters deliberately: both answer questions about the
 * same store and program, and a lead who narrows to one store then switches tabs is asking the same
 * question about work that has not run yet.
 */
@Component({
    name: "BillingReviewQueuePage",
    components: {
        SearchComponent,
        BottomPagerBar,
        BillingReviewDetails,
    },
})
export default class BillingReviewQueuePage extends Vue {
    private reviews: Array<BillingReviewRow> = [];
    private scheduled: Array<ScheduledClaimRow> = [];
    private storeFilter: Store = new Store();
    private programFilter: Program = new Program();
    private selected: BillingReviewRow | null = null;

    protected pageNumber: number = 1;
    protected scheduledPageNumber: number = 1;
    protected pageSize: number = 100;
    protected loading: boolean = true;

    private sortBy: string = "fillDate";
    private sortDesc: boolean = false;
    private scheduledSortBy: string = "fillDate";
    private scheduledSortDesc: boolean = false;

    // Restoring saved filters assigns them, which trips the same watcher that saves them. Without
    // this the restore writes back what it just read, on every visit.
    private skipFilterSave = false;

    private currencyFormatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

    get reviewFields() {
        return [
            { key: "rxId", label: "Rx" },
            { key: "fillDate", sortable: true, formatter: (d: any) => (d ? moment(d).format("L") : "") },
            { key: "patientName", sortable: true, label: "Patient" },
            { key: "drugName", sortable: true, label: "Drug" },
            // Blank rather than $0.00 for a fill with no patient pay, matching how the detail view
            // renders its null amounts. Intl coerces null to 0, which would show an unpriced fill as
            // a genuine zero copay.
            { key: "copay", sortable: true, formatter: (c: any) => (c === null || c === undefined ? "" : this.currencyFormatter.format(c)) },
            { key: "programName", sortable: true, label: "Program" },
            { key: "storeName", sortable: true, label: "Store" },
            { key: "billingReviewReason", sortable: true, label: "Reason" },
            { key: "review", label: "" },
        ];
    }

    get scheduledFields() {
        return [
            { key: "rxId", label: "Rx" },
            { key: "fillDate", sortable: true, formatter: (d: any) => (d ? moment(d).format("L") : "") },
            { key: "patientName", sortable: true, label: "Patient" },
            { key: "drugName", sortable: true, label: "Drug" },
            { key: "planName", sortable: true, label: "Plan" },
            { key: "sequencePosition", sortable: true, label: "Seq" },
            { key: "expectedOutcomeLabel", sortable: true, label: "Expected" },
            { key: "programName", sortable: true, label: "Program" },
            { key: "storeName", sortable: true, label: "Store" },
            { key: "scheduledBy", sortable: true, label: "Scheduled By" },
        ];
    }

    get sortedReviews() {
        return sortRows(this.reviews, this.sortBy, this.sortDesc);
    }

    get pagedReviews() {
        return pageRows(this.sortedReviews, this.pageNumber, this.pageSize);
    }

    get sortedScheduled() {
        return sortRows(this.scheduled, this.scheduledSortBy, this.scheduledSortDesc);
    }

    get pagedScheduled() {
        return pageRows(this.sortedScheduled, this.scheduledPageNumber, this.pageSize);
    }

    prescriptionRoute(row: BillingReviewRow | ScheduledClaimRow) {
        return {
            name: "PrescriptionDetails",
            params: {
                storeID: row.storeID,
                rxNumber: row.rxNumber,
                rfNumber: row.rfNumber,
            },
        };
    }

    @Watch("storeFilter")
    @Watch("programFilter")
    onFilterChanged() {
        this.persistFilters();
        this.pageNumber = 1;
        this.scheduledPageNumber = 1;
    }

    @Watch("pageSize")
    onPageSizeChanged() {
        savePageSizePreference(this, "pageSize_BillingReview", this.pageSize);
    }

    created() {
        this.pageSize = getPageSizePreference(this, "pageSize_BillingReview", 100);
        this.restoreFilters();
        this.search();
    }

    private restoreFilters() {
        const saved = loadQueueFilters(this, "billingReviewQueue_filters");
        if (!saved) return;
        this.skipFilterSave = true;
        if (saved.store?.id) this.storeFilter = new Store(saved.store);
        if (saved.program?.id) this.programFilter = new Program(saved.program);
        this.$nextTick(() => { this.skipFilterSave = false; });
    }

    private persistFilters() {
        if (this.skipFilterSave) return;
        saveQueueFilters(this, "billingReviewQueue_filters", {
            store: this.storeFilter?.id ? { id: this.storeFilter.id, name: this.storeFilter.name } : null,
            program: this.programFilter?.id ? { id: this.programFilter.id, name: this.programFilter.name } : null,
        });
    }

    // Both tabs are fetched together: they are two views of the same filter, and loading the second
    // only on tab switch would put a spinner in front of a lead who is toggling between them.
    async search() {
        const params = {
            storeId: this.storeFilter?.id ?? 0,
            programId: this.programFilter?.id ?? 0,
        };

        try {
            this.loading = true;
            const [reviewResp, scheduledResp] = await Promise.all([
                Axios.get("/BillingReview/Queue", { params }),
                Axios.get("/BillingReview/Scheduled", { params }),
            ]);

            this.reviews = reviewResp.data.map((r: any) => new BillingReviewRow(r));
            this.scheduled = scheduledResp.data.map((s: any) => new ScheduledClaimRow(s));
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
            console.warn("Error while fetching the billing review queue", error);
        } finally {
            this.loading = false;
        }
    }

    handleSort(event: any) {
        this.sortBy = event.sortBy;
        this.sortDesc = event.sortDesc;
        this.pageNumber = 1;
    }

    handleScheduledSort(event: any) {
        this.scheduledSortBy = event.sortBy;
        this.scheduledSortDesc = event.sortDesc;
        this.scheduledPageNumber = 1;
    }

    openDetail(row: BillingReviewRow) {
        this.selected = row;
        this.$bvModal.show("billing-review-modal");
    }

    // Drops the completed fill locally rather than refetching, so the row the reviewer just cleared
    // does not reappear for the moment it takes the queue to come back.
    onReviewCompleted(rxId: string) {
        this.reviews = this.reviews.filter(r => r.rxId !== rxId);
        this.selected = null;
        this.$bvModal.hide("billing-review-modal");
    }
}
</script>

<style scoped></style>
